import { useEffect, useState } from "react";
import { 
    getConfigurationsTestsByCompanyId, 
    updateConfigurationsTests
} from "../../../../features/search/service/config-tests";
import { getMe } from "../../../../features/search/service/user-service";
import { getLanguagesSelect } from "../../../../features/search/service/languages-service";
import { User } from "../../../../shared/types/user";
import { SelectOption } from "../../../../shared/types/select-options";
import InputComponent from "../../../../components/input/input";
import ButtonComponent from "../../../../components/button/button";
import SelectComponent from "../../../../components/input/select";
import { MenuTop } from '../../../../components/menu/top/menu-top';
import { useNavigate, useParams } from "react-router-dom";
import './styles/edit.css';
import ButtonSecondaryComponent from "../../../../components/button-secondary/button";
import { useTranslation } from "../../../../shared/i18n/i18n-context";

export const AdminConfigsEdit = () => {
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    const { t } = useTranslation('admin');

    const [me, setMe] = useState<User | null>(null);
    const [firstValue, setFirstValue] = useState<string>('');
    const [secondValue, setSecondValue] = useState<string>('');
    const [currency, setCurrency] = useState<string>('');
    const [languageId, setLanguageId] = useState<string>('');
    const [languages, setLanguages] = useState<SelectOption[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [loadingData, setLoadingData] = useState<boolean>(true);

    useEffect(() => {
        const fetchUser = async () => {
            try {
                const userData = await getMe();
                setMe(userData);
            } catch (error) {
                console.error('❌ Erro ao buscar dados do usuário:', error);
            }
        };
        fetchUser();
    }, []);

    useEffect(() => {
        if (!me || !id) return;

        const fetchData = async () => {
            try {
                setLoadingData(true);

                const languagesData = await getLanguagesSelect();
                setLanguages(languagesData);

                if (me.company?.id) {
                    const configsTests = await getConfigurationsTestsByCompanyId(me.company.id);
                    
                    const configToEdit = configsTests.find((config: any) => String(config.id) === id);
                    
                    if (configToEdit) {
                        setFirstValue(configToEdit.firstValue || '');
                        setSecondValue(configToEdit.secondValue || '');
                        setCurrency(configToEdit.currency || '');
                        
                        const langId = configToEdit.language_id;
                        setLanguageId(langId);
                    } else {
                        alert(t('configNotFound'));
                        navigate('/admin-configs-list');
                    }
                }
            } catch (error) {
                console.error('❌ Erro ao buscar dados:', error);
                alert(t('configLoadErrorShort'));
            } finally {
                setLoadingData(false);
            }
        };

        fetchData();
    }, [me, id, navigate, t]);

    const updateConfig = async () => {
        if (!firstValue || !secondValue || !currency || !languageId) {
            alert(t('fillAllFields'));
            return;
        }

        if (!id) {
            alert(t('configIdNotFound'));
            return;
        }

        const payload = {
            id,
            firstValue,
            secondValue,
            currency,
            languageId: languageId
        };

        setLoading(true);

        try {
            await updateConfigurationsTests(payload);
            
            navigate('/admin-configs-list');
        } catch (error) {
            console.error('❌ Erro ao atualizar configuração:', error);
            alert(t('updateConfigError'));
        } finally {
            setLoading(false);
        }
    };

    if (loadingData) {
        return (
            <div>
                <MenuTop />
                <div className="configs-container">
                    <h1>{t('loadingConfig')}</h1>
                </div>
            </div>
        );
    }
    
    return (
        <div>
            <MenuTop />
            <div className="configs-container">
                <h1>{t('editConfig')}</h1>
                
                <InputComponent 
                    id="id-input-firstValue" 
                    label={t('delayedValue')} 
                    value={firstValue} 
                    onChange={(event) => setFirstValue(event.target.value)} 
                />
                
                <InputComponent 
                    id="id-input-secondValue" 
                    label={t('immediateValue')} 
                    value={secondValue} 
                    onChange={(event) => setSecondValue(event.target.value)} 
                />
                
                <InputComponent 
                    id="id-input-currency" 
                    label={t('currency')} 
                    value={currency} 
                    onChange={(event) => setCurrency(event.target.value)}
                />
                
                    <SelectComponent
                        label={t('language')}
                        value={languageId}
                        onChange={(value) => {
                            console.log('📋 Idioma selecionado:', value);
                            setLanguageId(String(value));
                        }}
                        options={languages}
                        styles={{ marginTop: '10px' }}
                    />

                <div className="button-container">
                    <ButtonSecondaryComponent 
                        label={t('cancel')}
                        onClick={() => navigate('/admin-painel/list-configs')}
                    />
                    <ButtonComponent
                        label={loading ? t('saving') : t('saveChanges')}
                        onClick={updateConfig}
                        disabled={loading}
                    />
                </div>
            </div>
        </div>
    );
}
